
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { DeepRoleInterviewRequest, DeepRoleInterviewResponse } from "@/utils/a2a/types";

export interface DeepInterviewState {
  status: 'idle' | 'in-progress' | 'completed' | 'failed';
  currentQuestion: number;
  followupQuestions: any[] | null;
  assessment: any | null;
}

export function useDeepInterview() {
  const [state, setState] = useState<DeepInterviewState>({
    status: 'idle',
    currentQuestion: 0,
    followupQuestions: null,
    assessment: null,
  });

  const deepRoleInterview = async (request: DeepRoleInterviewRequest): Promise<DeepRoleInterviewResponse | null> => {
    try {
      setState(prev => ({ ...prev, status: 'in-progress' }));
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Authentication required to use screening agent");
      }
      
      const { data, error } = await supabase.functions.invoke('screening-agent/screening/deep-role-interview', {
        body: request,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (error) throw error;
      
      setState({ 
        status: data.isComplete ? 'completed' : 'in-progress',
        followupQuestions: data.followupQuestions,
        assessment: data.finalAssessment,
        currentQuestion: request.round || 0
      });
      
      if (data.isComplete) {
        toast.success("Deep role interview completed");
      } else {
        toast.info("Follow-up questions generated");
      }
      
      return data;
    } catch (err) {
      console.error("Error in deep role interview:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to process interview";
      toast.error(errorMessage);
      
      setState(prev => ({ ...prev, status: 'failed' }));
      return null;
    }
  };

  return {
    deepInterview: state,
    deepRoleInterview
  };
}
